<template>
  <div>
    <div class="payout-bulk-generation">
      <div class="row">
        <div class="col col-12 col-md-2">
          <label class="form__label">Date From:</label>
          <Datepicker
            class="datapicker"
            :bootstrap-styling="true"
            format="yyyy-MM-dd"
            :value="_toIsoStringTime(dateFrom)"
            @input="dateFrom = _toLocalTime($event, defaultDateFormat)"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">Date To:</label>
          <Datepicker
            class="datapicker"
            :bootstrap-styling="true"
            format="yyyy-MM-dd"
            :value="_toIsoStringTime(dateTo)"
            @input="dateTo = _toLocalTime($event, defaultDateFormat)"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">USD / UAH</label>
          <b-form-input
            type="number"
            v-model="currencyRates.UAH"
            debounce="300"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">USD / EUR</label>
          <b-form-input
            type="number"
            v-model="currencyRates.EUR"
            debounce="300"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">USD / GBP</label>
          <b-form-input
            type="number"
            v-model="currencyRates.GBP"
            debounce="300"
          />
        </div>
        <div class="col col-12 col-md-2 align-self-end">
          <b-button
            type="submit"
            class="button generate-payout-button"
            variant="primary"
            @click.prevent="generatePayouts">
            Generate payouts
          </b-button>
        </div>
      </div>
      <div class="d-flex justify-content-center">
        <div v-if="isRatesLoading" class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>

      <SlideOutContainerViaPortal>
        <div v-if="isShowCreationForm">
          <BulkCreationFormContainer
            title="Generated payouts"
            :onCloseButtonClick="hideCreationForm"
            :isLoading="isDataLoading"
            :breadcrumbs="[
              {
                text: 'Home',
                to: { name: 'home' },
              },
              {
                text: 'Payouts',
                click: () => closeCreationForm()
              }
            ]"
          >
            <CollapsiblePanel
              title="Generation based on non-covered worklogs"
              bodyInnerClassName="pt-1 pb-0"
            >
              <!-- Payees for creation info -->
              <div class="row mb-2">
                <div class="col flex-grow-0">
                  <SuccessIcon className="creation-info-success-icon" />
                </div>
                <div class="col flex-grow-1">
                  Fetched <b>{{ dataForCreation.length }} payees</b> (contractors and partners) for creation at
                  period:
                  {{ _toLocalTime(dateFrom, defaultDateFormat) }}
                  &ndash;
                  {{ _toLocalTime(dateTo, defaultDateFormat) }}
                </div>
              </div>

              <!-- Payees without compensation -->
              <div v-if="payeeNamesWithoutCompensations" class="row mb-2">
                <div class="col flex-grow-0">
                  <ErrorIcon />
                </div>
                <div class="col flex-grow-1">
                  Attention! Payees: <b>{{ payeeNamesWithoutCompensations.join(', ') }}</b> has no active
                  compensation at period:
                  {{ _toLocalTime(dateFrom, defaultDateFormat) }}
                  &ndash;
                  {{ _toLocalTime(this.dateTo, this.defaultDateFormat) }}.
                  Please create <a href="/finances-compensations" target="_blank">compensations</a> and "Generate Payouts" again.
                </div>
              </div>

              <BulkCreationTableBaseStyle v-if="messages.length">
                <table class="bulk-creation-table table-hover mt-4">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Creating</th>
                      <th>Payout for actual period</th>
                      <th>Status</th>
                      <th class="actions-column">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(message, index) in messages" :key="index">
                    <td>{{ index + 1 }}</td>
                    <td>
                      <strong v-if="message.isPartner">(Partner)</strong>
                      {{ message.payeeName }}
                    </td>
                    <td>{{ message.dateFrom }} &ndash; {{ message.dateTo }}</td>
                    <td class="status-column">
                      <span v-if="message.loading" class="small-spinner spinner-border" role="status">
                        <span class="sr-only">Creating...</span>
                      </span>
                      <SuccessIcon v-else-if="message.status === 'success'" />
                      <ErrorIcon
                        v-else-if="['error', 'warning'].includes(message.status)"
                        :tooltipHTML="message.errors && message.errors.join(' | ')"
                      />
                      <span
                        v-if="message.item && message.item.extra_payments.length"
                        class="ep-message"
                        v-b-tooltip.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                        :title="'This payout has extra payments copied from last payee payout'"
                      >
                        EP<InfoIcon />
                      </span>
                      <template v-if="message.item && message.item.indicators && Object.values(message.item.indicators).length">
                        <span
                          v-for="(items, indicator) in message.item.indicators"
                          :key="indicator"
                          class="ep-message"
                          v-b-tooltip.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                          :title="indicatorTooltip(items)"
                        >
                          {{ indicator }}<InfoIcon />
                        </span>
                      </template>
                    </td>
                    <td class="actions-column">
                      <PayoutsLinks
                        v-if="message.item"
                        :templates="templates"
                        :item="message.item"
                        @updatePayouts="updatePayouts"
                        @deletedPayout="deletePayout('auto', index)"
                        @showEditPayoutsModal="showPayoutFormManual(message)"
                        @markAsPaid="markPaid($event, message.item)"
                      />
                    </td>
                  </tr>
                  </tbody>
                </table>
              </BulkCreationTableBaseStyle>

              <div v-if="isButtonsBlockVisible" class="d-flex justify-content-center mt-4">
                <button
                  class="btn button btn-primary"
                  v-if="isStopCreationButtonVisible"
                  @click="isProcessedCreation = false">Stop creation ({{ payoutsForCreationCount }} left)</button>
                <button
                  v-if="isProcessedCreationButtonVisible"
                  class="btn button btn-primary"
                  @click="processCreation()">Create {{ payoutsForCreationCount }} payouts</button>
              </div>
            </CollapsiblePanel>

            <CollapsiblePanel
              title="Suggested payouts without worklogs (duplicate from last 2 months)"
              bodyInnerClassName="pt-0"
            >
              <BulkCreationTableBaseStyle v-if="copyExtraPaymentedPayouts.length">
                <table class="extra-paymented-payouts-table table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Date</th>
                      <th class="actions-column">Actions</th>
                      <th></th>
                      <th>Name</th>
                      <th>Date</th>
                      <th class="actions-column">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="({ original, item, status }, index) in copyExtraPaymentedPayouts"
                      :key="(original ? original.id : 0) + '_' + (item ? item.id : 0)"
                    >
                    <td>{{index + 1}}</td>
                    <td>
                      <template v-if="original">{{original.payee_name}}</template>
                    </td>
                    <td>
                      <template v-if="original">{{ _toLocalTime(original.issue_date, defaultDateFormat) }}</template>
                    </td>
                    <td class="actions-column">
                      <PayoutsLinks
                        v-if="original"
                        :item="original"
                        @updatePayouts="updatePayouts"
                        @deletedPayout="deletePayout('original', index)"
                        @showEditPayoutsModal="showPayoutFormManual({ item: original })"
                        @markAsPaid="markPaid($event, original)"
                      />
                    </td>
                    <td>
                      <CopyInvoiceButton v-if="status === 'new'" :onClick="() => copyPayout(index)" />
                      <div v-else-if="status === 'creating'" class="small-spinner spinner-border">
                        <span class="sr-only">Creating...</span>
                      </div>
                      <CopiedStatus v-else-if="status === 'created'" />
                    </td>
                    <td>
                      <template v-if="item">{{item.payee_name}}</template>
                    </td>
                    <td>
                      <template v-if="item">{{ _toLocalTime(item.issue_date, defaultDateFormat) }}</template>
                    </td>
                    <td class="actions-column">
                      <PayoutsLinks
                        v-if="item"
                        :item="item"
                        @updatePayouts="updatePayouts"
                        @deletedPayout="deletePayout('item', index)"
                        @showEditPayoutsModal="showPayoutFormManual(copyExtraPaymentedPayouts[index])"
                        @markAsPaid="markPaid($event, item)"
                      />
                    </td>
                  </tr>
                  </tbody>
                </table>
              </BulkCreationTableBaseStyle>

              <p v-else>No payouts</p>
            </CollapsiblePanel>
          </BulkCreationFormContainer>
        </div>
      </SlideOutContainerViaPortal>

      <FinancePayoutsForm
        ref="payoutFormManual"
        v-model="payoutFormManual.show"
        :mode="payoutFormManual.data.id ? 'edit' : 'create'"
        :data="payoutFormManual.data"
        :title="payoutFormManual.data.id && payoutFormManual.data.payout_number ? `Payout ${payoutFormManual.data.payout_number}` : (payoutFormManual.data.id ? 'Save Payout' : 'Create Payout')"
        :submitButtonText="payoutFormManual.data.id ? 'Save' : 'Create'"
        @submit="savePayoutManually"
        @close="onClose"
        :breadcrumbs="[
          {
            text: 'Home',
            to: { name: 'home' },
          },
          {
            text: 'Bulk payouts creation',
            click: () => onClose(),
          },
        ]"
      />

    <FinancePayoutsForm
      ref="payoutFormAuto"
      :value="false"
      mode="create"
    />
    <FinancePayoutsForm
      ref="payoutFormCopy"
      :value="false"
      mode="create"
    />
  </div>
</template>

<script>
import { cloneDeep, debounce } from 'lodash'
import Moment from 'moment'
import axios from 'axios'
import Datepicker from 'vuejs-datepicker'
import { permissions } from '@mocks/permissions'
import { BulkCreationConfirmTabCloseMixin } from '@mixins/invoices/BulkCreationConfirmTabCloseMixin'
import FinancePayoutsForm from '@components/modals/FinancePayoutsForm/FinancePayoutsForm'
import PayoutsLinks from '../components/PayoutsLinks'
import { PostalNames } from '@/constants/PortalNames'
import { STOP_BULK_CREATION_CONFIRMATION_MODAL_CONFIG } from '@/constants/modals/bulk-creation-modals'
import SlideOutContainerViaPortal from '@components/transition/SlideOutContainerViaPortal.vue'
import CollapsiblePanel from '@components/invoices/CollapsiblePanel.vue'
import BulkCreationFormContainer from '@components/invoices/bulk-creation/BulkCreationFormContainer.vue'
import BulkCreationTableBaseStyle from '@components/invoices/bulk-creation/BulkCreationTableBaseStyle.vue'
import { SuccessIcon, ErrorIcon, InfoIcon } from '@components/icons/'
import CopyInvoiceButton from '@components/invoices/bulk-creation/CopyInvoiceButton.vue'
import CopiedStatus from '@components/invoices/bulk-creation/CopiedStatus.vue'
import ScrollPositionMixin from '@mixins/ScrollPositionMixin'

export default {
  name: 'FinancePayoutsBulkCreation',
  mixins: [BulkCreationConfirmTabCloseMixin, ScrollPositionMixin],
  components: {
    BulkCreationFormContainer,
    SlideOutContainerViaPortal,
    CollapsiblePanel,
    Datepicker,
    FinancePayoutsForm,
    PayoutsLinks,
    SuccessIcon,
    ErrorIcon,
    InfoIcon,
    BulkCreationTableBaseStyle,
    CopyInvoiceButton,
    CopiedStatus
  },
  props: {
    templates: {
      type: Array
    }
  },
  data () {
    const defaultDateFormat = 'YYYY-MM-DD'
    return {
      permissions,
      defaultDateFormat,
      isProcessedCreation: false,
      openedPayoutMessage: null,
      payoutFormManual: {
        show: false,
        data: {}
      },
      isRatesLoading: false,
      isDataLoading: false,
      dateFrom: this._toLocalTime(Moment().subtract(1, 'months').startOf('month')),
      dateTo: this._toLocalTime(Moment().subtract(1, 'months').endOf('month')),
      currencyRates: {
        UAH: null, EUR: null, GBP: null
      },
      dataForCreation: [],
      copyExtraPaymentedPayouts: [],
      messages: [],
      PostalNames
    }
  },
  computed: {
    payeeDataWithoutCompensation () {
      return this.dataForCreation.filter(item => item.no_compensation)
    },
    payeeNamesWithoutCompensations () {
      if (!this.payeeDataWithoutCompensation.length) return null

      return this.payeeDataWithoutCompensation.map(item => {
        if (item.contractor) {
          return item.contractor.name
        }
        if (item.partner) {
          return '[Partner] ' + item.partner.company_name
        }
      })
    },
    payoutsForCreationCount () {
      return this.dataForCreation.filter(item => !item.no_compensation && !item.processed).length
    },
    isSomePayoutCreating () {
      return this.messages.some(item => item.loading)
    },
    isStopCreationButtonVisible () {
      return this.isProcessedCreation && this.payoutsForCreationCount > 0
    },
    isProcessedCreationButtonVisible () {
      return !this.isStopCreationButtonVisible && this.payoutsForCreationCount > 0 && !this.isSomePayoutCreating
    },
    isButtonsBlockVisible () {
      return this.isStopCreationButtonVisible || this.isProcessedCreationButtonVisible
    }
  },
  created () {
    this.fetchActualRate()
  },
  methods: {
    Moment: function (date) {
      return Moment(date)
    },
    async generatePayouts () {
      this.isShowCreationForm = true

      this.dataForCreation = []
      this.copyExtraPaymentedPayouts = []
      this.messages = []

      this.isDataLoading = true
      const dataForCreationResponse = await this.fetchDataForCreation()
      this.isDataLoading = false

      this.dataForCreation = dataForCreationResponse.data.generatedByLogTimes.map(item => {
        item.processed = false
        return item
      })
      this.copyExtraPaymentedPayouts = dataForCreationResponse.data.copyExtraPaymented.map(item => {
        return {
          original: item,
          item: null,
          status: 'new'
        }
      })
    },
    closeCreationForm () {
      this.isShowCreationForm = false
      this.isProcessedCreation = false
    },
    async hideCreationForm () {
      const confirm = await this.$root.$confirm.open(...STOP_BULK_CREATION_CONFIRMATION_MODAL_CONFIG)
      if (confirm) {
        this.isShowCreationForm = false
      }

      this.isProcessedCreation = false
      if (this.messages.filter(item => item.type === 'creation' && item.status === 'success').length > 0) {
        this.updatePayouts()
      }
    },
    updatePayouts () {
      this.$emit('updatePayouts')
    },
    deletePayout (type, index) {
      if (type === 'auto') {
        this.messages.splice(index, 1)
      } else if (type === 'item') {
        if (this.copyExtraPaymentedPayouts[index]['original'] === null) {
          this.copyExtraPaymentedPayouts.splice(index, 1)
        } else {
          this.copyExtraPaymentedPayouts[index]['item'] = null
          this.copyExtraPaymentedPayouts[index].status = 'new'
        }
      } else if (type === 'original') {
        if (this.copyExtraPaymentedPayouts[index]['item'] === null) {
          this.copyExtraPaymentedPayouts.splice(index, 1)
        } else {
          this.copyExtraPaymentedPayouts[index]['original'] = null
        }
      }
    },
    saveMessageSuccess (message, item) {
      message.status = 'success'
      message.item = item ? Object.assign(message.item, item) : message.item
      message.errors = []
      message.loading = false
    },
    saveMessageError (message, errors, item) {
      message.status = 'error'
      message.errors = message.errors ? message.errors.push(errors) : errors
      message.loading = false
      message.item = item ? Object.assign(message.item, item) : message.item
    },
    async processCreation () {
      this.isProcessedCreation = true

      const dateFrom = this.dateFrom
      const dateTo = this.dateTo

      for (const index in this.dataForCreation) {
        const item = this.dataForCreation[index]
        if (!this.isProcessedCreation) {
          break
        }
        if (item.processed || item.no_compensation) {
          continue
        }

        const contractorId = item.contractor ? item.contractor.id : null
        const partnerId = item.partner ? item.partner.id : null

        this.messages.push({
          type: 'creation',
          status: 'in-progress',
          payeeName: item.partner ? item.partner.company_name : item.contractor.name,
          isPartner: Boolean(item.partner),
          dateFrom: this._toLocalTime(dateFrom, this.defaultDateFormat),
          dateTo: this._toLocalTime(dateTo, this.defaultDateFormat),
          loading: true
        })

        item.processed = true
        this.dataForCreation[index] = item

        let processedPayoutMessage = this.messages.at(-1)

        const preFillData = await this.$refs['payoutFormAuto'].fetchPreFillPayoutData(contractorId, partnerId)

        let payoutData = preFillData.data
        payoutData.items = []
        payoutData.auto_fill = { date_from: dateFrom, date_to: dateTo }

        payoutData.comment = this.$refs['payoutFormAuto'].generateCommentText(payoutData.auto_fill, 'comment')
        payoutData.comment_national = this.$refs['payoutFormAuto'].generateCommentText(payoutData.auto_fill, 'comment_national')

        payoutData.payout_status = this.getPayoutStatus('new')
        processedPayoutMessage.item = payoutData

        let errors = []
        if (!payoutData.payee_bank_details) {
          errors.push('Payee bank details is required')
        }

        if (!payoutData.own_bank_details) {
          errors.push('Own bank details is required')
        }

        if (errors.length > 0) {
          this.saveMessageError(processedPayoutMessage, errors, payoutData)
          continue
        }

        const currencyCode = payoutData.payee_bank_details.currency.code
        const currencyRate = this.currencyRates[currencyCode]
        payoutData.currency_rate = currencyRate.toString()

        this.$refs['payoutFormAuto'].setParamsForEdit(payoutData)

        if (payoutData.payout_type.id === 1) {
          const autoFillData = await this.axios.post(
            `/api/finances/payouts/auto-fill-request`,
            {
              payout_id: null, contractor_id: contractorId, partner_id: partnerId, date_from: dateFrom, date_to: dateTo, currency_code: currencyCode, currency_rate: currencyRate
            },
            { headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') } }
          )

          if (autoFillData.data.length) {
            payoutData.items = payoutData.contractors = autoFillData.data
            await this.$refs['payoutFormAuto'].autoFillItems(autoFillData.data)
          } else {
            this.saveMessageError(processedPayoutMessage, ['No contractor payout items at period'], payoutData)
            continue
          }
        }
        const payoutDataForRequest = this.$refs['payoutFormAuto'].getPreparedDataForRequest()

        payoutData.indicators = cloneDeep(this.$refs['payoutFormAuto'].form.fields.indicators.value)
        processedPayoutMessage.item = payoutData
        await this.savePayoutBulk(payoutDataForRequest, processedPayoutMessage)
      }
    },
    async savePayoutBulk (payoutDataForRequest, payoutMessage) {
      return this.savePayout(payoutDataForRequest, payoutMessage, async function () {
        if (payoutDataForRequest.id) {
          return axios.put(`/api/finances/payouts/${payoutDataForRequest.id}`, payoutDataForRequest, {
            headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') }
          })
        } else {
          return axios.post(`/api/finances/payouts`, payoutDataForRequest, {
            headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') }
          })
        }
      })
    },
    async savePayoutManually (payoutDataForRequest) {
      const that = this
      await this.savePayout(payoutDataForRequest, this.openedPayoutMessage, async function () {
        if (payoutDataForRequest.id) {
          return that._toggleDataPageLoading(async () => {
            return that.$api.finances.updatePayouts(payoutDataForRequest)
          })
        } else {
          return that._toggleDataPageLoading(async () => {
            return that.$api.finances.createPayouts(payoutDataForRequest)
          })
        }
      })
      this.payoutFormManual.show = false
      this.updatePayouts()
    },
    async savePayout (payoutDataForRequest, payoutMessage, savePayoutFunc) {
      try {
        let payoutResponse
        payoutResponse = await savePayoutFunc()
        const payoutData = payoutResponse.data
        payoutData.auto_fill = payoutMessage.item.auto_fill

        if (payoutResponse.status === 200) {
          this.saveMessageSuccess(payoutMessage, payoutData)
        }
      } catch (e) {
        const errors = []
        if (e.response.data.errors) {
          errors.push(Object.values(e.response.data.errors).map(itemErrors => itemErrors[0]))
        } else {
          errors.push(e.message)
        }

        this.saveMessageError(payoutMessage, errors)
      }
    },
    async markPaid (payout, item) {
      if (!payout.id) return
      this._toggleDataPageLoading(async () => {
        const marked = await this.$api.finances.markPayoutPaid(payout.id)
        if (marked.data) {
          this.$snotify.success(
            'Payout successfully marked as paid',
            'Success',
            this.$store.state.snotify
          )
          item.payout_status = this.getPayoutStatus('paid')
          item.is_paid = true
        }
      })
    },
    getPayoutStatus (name) {
      return this.$refs['payoutFormAuto'].getPayoutsStatuses.find(el => el.name === name)
    },
    fetchActualRate: debounce(function () {
      this.isRatesLoading = true
      this.axios
        .get('/api/finances/currencies/actual-rates-for-date', {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then((response) => {
          this.currencyRates = response.data
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isRatesLoading = false
        })
    }, 1500),
    fetchDataForCreation () {
      return this.axios
        .post('/api/finances/payouts/data-for-bulk-creation',
          {
            date_from: this._toLocalTime(this.dateFrom),
            date_to: this._toLocalTime(this.dateTo)
          },
          { headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') } })
        .catch((error) => {
          this.errorNotifications(error)
        })
    },
    async showPayoutFormManual (message) {
      if (this.$can(this.permissions.UpdateAllFinancesPayouts)) {
        this.openedPayoutMessage = message
        this.payoutFormManual.show = true
        this.scrollToTheTop()

        this.payoutFormManual.data = await cloneDeep(message.item)
      }
    },
    async copyPayout (index) {
      const original = this.copyExtraPaymentedPayouts[index].original
      this.copyExtraPaymentedPayouts[index].status = 'creating'
      const item = cloneDeep(original)
      item.payout_status = this.getPayoutStatus('new')
      this.$refs['payoutFormCopy'].setParamsForEdit(item)

      const payoutDataForRequest = this.$refs['payoutFormCopy'].getPreparedDataForRequest()
      const payoutIntervalDates = {
        date_from: this.dateFrom,
        date_to: this.dateTo
      }
      payoutDataForRequest.comment = this.$refs['payoutFormCopy'].generateCommentText(payoutIntervalDates, 'comment')
      payoutDataForRequest.comment_national = this.$refs['payoutFormCopy'].generateCommentText(payoutIntervalDates, 'comment_national')
      // payoutDataForRequest.items = []
      // payoutDataForRequest.payout_type_id = 2
      payoutDataForRequest.issue_date = this.Moment().format(this.defaultDateFormat)
      payoutDataForRequest.due_date = this.Moment().add(3, 'days').format(this.defaultDateFormat)
      payoutDataForRequest.paid_date = ''

      const payoutResponse = await this._toggleDataPageLoading(async () => {
        return this.$api.finances.createPayouts(payoutDataForRequest)
      })
      this.copyExtraPaymentedPayouts[index].item = payoutResponse.data
      this.copyExtraPaymentedPayouts[index].status = 'created'
    },
    indicatorTooltip (items) {
      const itemsAsString = Object.entries(items).map(
        ([name, hours]) => {
          return name + ' (working: ' + hours.working_hours + '; logged: ' + hours.logged_hours + ')'
        }
      )
      return itemsAsString.join(', ')
    },
    onClose () {
      this.payoutFormManual.show = false
      const route = this.$router.resolve({
        name: this.$route.name,
        params: this.$route.params
      })
      window.history.pushState({}, '', route.href)
    }
  }
}
</script>

<style lang="scss" scoped>
.generate-payout-button {
  width: 100%;
  justify-content: center;
}

.creation-info-success-icon {
  margin-top: -3px;
}

.bulk-creation-table {
  td, th {
    width: 33%;

    &:first-child {
      width: 0;
    }

    &:last-child {
      min-width: 136px;
    }
  }
}

.extra-paymented-payouts-table {
  td, th {
    width: calc(100% / 6);

    &:first-child {
      width: 0;
    }

    &:last-child {
      min-width: 136px;
    }
  }
}
</style>
